/**
 * "Download Today's Report" button.
 *
 * Visible to Admin and Doctor roles. Generates the daily report .xlsx from
 * the offline Dexie DB (no API call) and triggers a browser download.
 *
 * Date picker is restricted to the last 30 days; older dates redirect to
 * /reports for the historical-export flow (S8).
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DateInput } from "./ui/DateInput";

import { exportDailyReport, REPORT_RETENTION_DAYS } from "../utils/exportToExcel";
import { logger } from "../utils/logger";
import { useRole } from "../hooks/useRole";

function todayISODate(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function minPickableDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - REPORT_RETENTION_DAYS);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface DownloadReportButtonProps {
  /** Show the button only when the current user has one of these roles. */
  userRole: string | null;
  className?: string;
}

const ALLOWED_ROLES = new Set(["Admin", "Doctor"]);

const DownloadReportButton: React.FC<DownloadReportButtonProps> = ({ userRole, className }) => {
  const navigate = useNavigate();
  const { canOr } = useRole();
  const [date, setDate] = useState<string>(todayISODate());
  const [busy, setBusy] = useState(false);

  // The role check is the legacy fallback for a session whose /auth/me predates
  // permission_codes; the grant is authoritative once we have it.
  //
  // ADVISORY ONLY. Unlike the Settings backup — which calls
  // SettingsAPI.recordBackupExport first and fails closed on 403 — this report is
  // built entirely from Dexie with no request, so there is nothing for the server
  // to refuse. Hiding the button is the only enforcement available today; making
  // reports.export_daily real needs an authorize+audit endpoint on the
  // recordBackupExport pattern.
  if (!canOr("reports.export_daily", !!userRole && ALLOWED_ROLES.has(userRole))) return null;

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const target = new Date(date);
      target.setHours(0, 0, 0, 0);
      const result = await exportDailyReport(target);

      if (result.success) {
        toast.success(`Report downloaded: ${result.filename}`);
      } else if (result.reason === "no_activity") {
        toast.info("No activity recorded today");
      } else if (result.reason === "too_old") {
        toast.info("Historical reports available in Reports section");
        navigate("/reports");
      }
    } catch (err) {
      // PHI-safe logger contract: only primitives. Pass the error name (a
      // bounded string like "DexieError" or "DataError"), never the full
      // Error object — its message / stack could carry record IDs from the
      // offline DB error path.
      logger.error("DownloadReportButton.exportDailyReport failed", err instanceof Error ? err : undefined);
      toast.error("Failed to generate report");
    } finally {
      setBusy(false);
    }
  };

  // Tooltip for the button when the picked date is older than the cutoff.
  // The date input itself has min/max attributes, so this is a defensive
  // hint for users who type a date manually.
  const beyondRetention = new Date(date) < new Date(minPickableDate());

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className ?? ""}`}>
      <DateInput
        value={date}
        min={minPickableDate()}
        max={todayISODate()}
        onChange={e => setDate(e.target.value)}
        className="rounded-md border border-slate-300 px-2 py-1 min-h-[44px] text-base sm:text-sm"
        aria-label="Report date"
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        title={beyondRetention ? "Historical reports available in Reports section" : undefined}
        className="rounded-full bg-[#0066ff] px-4 min-h-[44px] text-sm font-medium text-white hover:bg-[#0051cc] disabled:opacity-50"
      >
        {busy ? "Generating…" : "Download Report"}
      </button>
    </div>
  );
};

export default DownloadReportButton;
